import { useEffect, useRef, useState } from "react";

import {
  Badge,
  Empty,
  Heading,
  Hr,
  Notification,
  Span,
  TelephoneMask,
  TextInput,
} from "@icg360/design-system";

import { MSSDrawer } from "components/mss-drawer";
import {
  useInspectionsList,
  usePolicyDetail,
  useSelectedPolicy,
  useUser,
} from "data/ss-data-hooks";
import { useFlags } from "utils";

import { PartnershipCard } from "./partnership-card";
import styles from "./roof-marketplace-drawer.module.scss";
import {
  buildRoofMarketplaceBidRequestResult,
  ROOF_MARKETPLACE_BID_FAILED,
  ROOF_MARKETPLACE_VALIDATION_TITLE,
} from "./roof-marketplace.ts";
import type {
  RoofMarketplaceBidRequest,
  RoofMarketplaceFieldErrors,
} from "./roof-marketplace.ts";

export type {
  RoofMarketplaceBidRequest,
  RoofMarketplaceFieldErrors,
} from "./roof-marketplace.ts";
export {
  isQualifyingRoofMarketplaceReferral,
  isRoofMarketplaceEligible,
  ROOF_MARKETPLACE_BID_FAILED,
  ROOF_MARKETPLACE_VALIDATION_TITLE,
} from "./roof-marketplace.ts";

const HOW_IT_WORKS_STEPS = [
  {
    title: "Compare fast, free bids from vetted contractors",
    description:
      "Within 24 to 48 hours you'll receive bids from reputable contractors who meet RoofMarketplace's strict quality standards.",
  },
  {
    title: "Get competitive, transparent pricing",
    description:
      "RoofMarketplace ensures every bid accurately reflects the work needed with a full cost breakdown and zero hidden fees.",
  },
  {
    title: "Support from start to finish",
    description:
      "A project coordinator manages the job from beginning to end and sends SageSure proof of completion to keep your policy active.",
  },
] as const;

const LEGAL_DISCLOSURE_ITEMS = [
  {
    label: "Service Independence",
    body: "SageSure provides access to RoofMarketplace as a courtesy to simplify your search. SageSure does not directly guarantee the performance of any contractor or the services, content, or functionality of RoofMarketplace. Its platform is subject to its own terms and conditions. Use of this service and engagement with or through RoofMarketplace is at your discretion.",
  },
  {
    label: "Communication",
    body: "If you choose to proceed with a bid, RoofMarketplace will manage all subsequent communications and project coordination.",
  },
  {
    label: "Data Privacy",
    body: "To generate your bids, SageSure shares limited property information with RoofMarketplace. Any personal data you provide directly to them is protected under their specific privacy policy.",
  },
] as const;

const ROOF_MARKETPLACE_LEARN_MORE_URL =
  "https://roofmarketplace.com/solutions-property-owner/";

type RoofMarketplaceDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * When true the drawer opens directly to the success screen, indicating a bid
   * has already been submitted for the current qualifying referrals.
   */
  hasExistingBid?: boolean;
  /**
   * Submit the bid request. Return `true` on API success so the drawer can show
   * the confirmation state; `false` on failure.
   */
  onRequestBids?: (
    payload: RoofMarketplaceBidRequest,
  ) => boolean | Promise<boolean>;
};

export const RoofMarketplaceDrawer = ({
  isOpen,
  onClose,
  hasExistingBid = false,
  onRequestBids,
}: RoofMarketplaceDrawerProps) => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successEmail, setSuccessEmail] = useState("");
  const [showBidRequestError, setShowBidRequestError] = useState(false);
  const [validationAlertMessage, setValidationAlertMessage] = useState<
    string | null
  >(null);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<RoofMarketplaceFieldErrors>
  >({});
  const { inspectionsProofUpload } = useFlags();
  const { data: policyDetail } = usePolicyDetail();
  const { data: inspectionsList } = useInspectionsList(
    !!inspectionsProofUpload,
  );
  const { data: selectedPolicy } = useSelectedPolicy();
  const user = useUser();
  const prevIsOpenRef = useRef(false);
  const formContentTopRef = useRef<HTMLDivElement>(null);

  const queueScrollFormContentToTop = () => {
    requestAnimationFrame(() => {
      formContentTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  useEffect(() => {
    if (!isOpen) {
      prevIsOpenRef.current = false;
      setShowBidRequestError(false);
      setValidationAlertMessage(null);
      setFieldErrors({});
      return;
    }

    const pi = policyDetail?.userDetails?.primaryInsured;
    const prefEmail =
      (user?.email ?? "").trim() || (pi?.emailAddress ?? "").trim() || "";
    const prefPhone = (pi?.phoneNumber ?? "").trim() || "";

    if (!prevIsOpenRef.current) {
      setEmail(prefEmail);
      setPhone(prefPhone);
      setShowBidRequestError(false);
      setValidationAlertMessage(null);
      setFieldErrors({});
      if (hasExistingBid) {
        setSuccessEmail(prefEmail);
        setShowSuccess(true);
      } else {
        setShowSuccess(false);
        setSuccessEmail("");
      }
    } else {
      setEmail((current) => current.trim() || prefEmail);
      setPhone((current) => current.trim() || prefPhone);
    }
    prevIsOpenRef.current = true;
  }, [isOpen, policyDetail, user, hasExistingBid]);

  const handleRequestBids = async () => {
    setValidationAlertMessage(null);
    setFieldErrors({});

    const built = buildRoofMarketplaceBidRequestResult({
      policyDetail,
      inspectionsData: inspectionsList,
      selectedPolicy,
      user: user
        ? {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
          }
        : undefined,
      formEmail: email,
      formPhone: phone,
    });
    if (!built.ok) {
      setShowBidRequestError(false);
      if (built.showBanner) {
        setValidationAlertMessage(built.message);
      } else {
        setValidationAlertMessage(null);
      }
      setFieldErrors(built.fieldErrors ?? {});
      if (built.showBanner) {
        queueScrollFormContentToTop();
      }
      return;
    }

    const payload = built.payload;
    if (!onRequestBids) {
      return;
    }
    setShowBidRequestError(false);
    setFieldErrors({});
    setIsSubmitting(true);
    try {
      const ok = await Promise.resolve(onRequestBids(payload));
      if (ok) {
        setSuccessEmail(payload.pointOfContact.emailAddress);
        setShowSuccess(true);
      } else {
        setShowBidRequestError(true);
        queueScrollFormContentToTop();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    onClose();
  };

  const openLearnMore = () => {
    window.open(
      ROOF_MARKETPLACE_LEARN_MORE_URL,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <MSSDrawer isOpen={isOpen} onClose={onClose}>
      <MSSDrawer.Header title="Roof replacement" onClose={onClose} />
      {showSuccess ? (
        <>
          <MSSDrawer.Body className={styles.successDrawerBody}>
            <div className={styles.successState}>
              <Empty
                duotoneIcon="Done"
                heading="Your bid request has been sent!"
                large
              >
                <Span color="default" className="text-sm">
                  RoofMarketplace will email your bids to{" "}
                  <Span bold>{successEmail}</Span> within 24 to 48 hours.
                </Span>
              </Empty>
            </div>
          </MSSDrawer.Body>
          <MSSDrawer.Footer
            primaryLabel="Close"
            onPrimary={handleSuccessClose}
            primaryButtonTestId="roof-marketplace-success-close"
          />
        </>
      ) : (
        <>
          <MSSDrawer.Body>
            <div ref={formContentTopRef} className={styles.drawerContent}>
              {validationAlertMessage ? (
                <div
                  className={styles.bidRequestErrorAlert}
                  data-testid="roof-marketplace-validation-alert"
                >
                  <Notification
                    appearance="danger"
                    description={validationAlertMessage}
                    title={ROOF_MARKETPLACE_VALIDATION_TITLE}
                    variant="banner"
                  />
                </div>
              ) : showBidRequestError ? (
                <div
                  className={styles.bidRequestErrorAlert}
                  data-testid="roof-marketplace-bid-error-alert"
                >
                  <Notification
                    appearance="danger"
                    description={ROOF_MARKETPLACE_BID_FAILED.description}
                    title={ROOF_MARKETPLACE_BID_FAILED.title}
                    variant="banner"
                  />
                </div>
              ) : null}
              <PartnershipCard />
              <Heading size="sm" className={styles.sectionHeading}>
                How it works
              </Heading>
              <ol className={styles.stepList}>
                {HOW_IT_WORKS_STEPS.map((step, index) => (
                  <li key={index} className={styles.stepItem}>
                    <span className={styles.stepBadge}>
                      <Badge.Numeric
                        number={index + 1}
                        size="medium"
                        appearance="neutral"
                      />
                    </span>
                    <div className={styles.stepContent}>
                      <Span bold className={styles.stepTitle}>
                        {step.title}
                      </Span>
                      <Span className={styles.stepDescription}>
                        {step.description}
                      </Span>
                    </div>
                  </li>
                ))}
              </ol>

              <Hr spacing="base" />

              <section className={styles.getStartedSection}>
                <Heading size="sm" className={styles.sectionHeading}>
                  Let's get started
                </Heading>
                <Span className={styles.getStartedIntro}>
                  Confirm your contact details and RoofMarketplace will send
                  your bids to the email you provide within 24 to 48 hours.
                </Span>
                <div className={styles.contactForm}>
                  <TextInput
                    label="Email"
                    name="roofMarketplaceEmail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    isRequired
                    isError={!!fieldErrors.email}
                    errorMessage={fieldErrors.email}
                    className={styles.contactField}
                  />
                  <TelephoneMask
                    label="Phone number"
                    name="roofMarketplacePhone"
                    value={phone.replace(/\D+/g, "")}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder=""
                    isRequired
                    isError={!!fieldErrors.phone}
                    errorMessage={fieldErrors.phone}
                    className={styles.contactField}
                    data-testid="roof-marketplace-phone"
                  />
                </div>
              </section>

              <section className={styles.legalSection}>
                <Span
                  size="sm"
                  medium
                  color="quiet"
                  className={styles.legalSectionTitle}
                >
                  Accessing RoofMarketplace: Important Disclosures
                </Span>
                <ul className={styles.legalList}>
                  {LEGAL_DISCLOSURE_ITEMS.map((item) => (
                    <li key={item.label} className={styles.legalListItem}>
                      <Span size="sm" color="quiet">
                        {item.label}: {item.body}
                      </Span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </MSSDrawer.Body>
          <MSSDrawer.Footer
            tertiaryLabel="Cancel"
            onTertiary={onClose}
            tertiaryDisabled={isSubmitting}
            tertiaryButtonTestId="roof-marketplace-cancel"
            secondaryLabel="Learn more"
            onSecondary={openLearnMore}
            secondaryButtonTestId="roof-marketplace-learn-more"
            primaryLabel="Get bids"
            onPrimary={handleRequestBids}
            primaryLoading={isSubmitting}
            primaryDisabled={isSubmitting}
            primaryButtonTestId="roof-marketplace-request-bids"
          />
        </>
      )}
    </MSSDrawer>
  );
};
